import React from 'react';
import ReactDOM from 'react-dom/client';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './i18n';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#3f9c46',
      light: '#81c784',
      dark: '#2e7d34',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#a2d149',
      light: '#c5e17a',
      dark: '#7cb02f',
      contrastText: '#ffffff',
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
    },
    warning: {
      main: '#ed6c02',
      light: '#ff9800',
      dark: '#e65100',
    },
    error: {
      main: '#d32f2f',
      light: '#ef5350',
      dark: '#c62828',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    divider: '#e2e8f0',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 10,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(15, 23, 42, 0.06)',
    '0 2px 6px rgba(15, 23, 42, 0.08)',
    '0 4px 12px rgba(15, 23, 42, 0.12)',
    '0 6px 14px rgba(15, 23, 42, 0.14)',
    '0 8px 18px rgba(15, 23, 42, 0.16)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
    '0 10px 22px rgba(15, 23, 42, 0.18)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          minHeight: 38,
          padding: '8px 14px',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          boxShadow: 'none',
          border: '1px solid #e2e8f0',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },
        indicator: {
          height: 3,
          borderRadius: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 44,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter future={{ v7_relativeSplatPath: true }}>
        <App />
      </BrowserRouter>
    </ThemeProvider>
  </React.StrictMode>
);

